import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, XCircle, RefreshCw, Download, Package, Plus, Trash2, Pause, Play } from 'lucide-react';
import * as XLSX from 'xlsx';
import authService from '../services/AuthService';
import MegaExport from '../components/MegaExport';
import { API_BASE } from '../consts';
import DealsViewer from '../components/DealsViewer';
import UpdateDealsOnboardingModal from '../components/UpdateDealsOnboardingModal';

const WholesaleScanPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'uploads'
    const [uploads, setUploads] = useState([]);
    const [loadingUploads, setLoadingUploads] = useState(false);
    const [selectedUpload, setSelectedUpload] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const currentPageRef = React.useRef(1);
    const [hasMore, setHasMore] = useState(false);
    const [totalUploads, setTotalUploads] = useState(0);
    const [pageSize, setPageSize] = useState(7);
    const pageSizeRef = React.useRef(7);

    // Upload Form States
    const [step, setStep] = useState(1); // 1: Upload, 2: Mapping, 3: Validation
    const [file, setFile] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({
        price: '',
        identifier: '',
        identifier_type: 'auto',
        image: '',
        source_link: ''
    });
    const [validatedData, setValidatedData] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [uploadName, setUploadName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    // Load uploads on mount
    useEffect(() => {
        loadUploads();
    }, []);

    // Auto-refresh processing uploads every 20 seconds
    useEffect(() => {
        const hasProcessingUploads = uploads.some(u => u.status === 'processing' || u.status === 'pending');

        if (!hasProcessingUploads) return;

        const interval = setInterval(() => {
            loadUploads(true);
        }, 20000);

        return () => clearInterval(interval);
    }, [uploads]);

    const loadUploads = async (silent = false, page = currentPageRef.current, size = pageSizeRef.current) => {
        if (!silent) setLoadingUploads(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/wholesale/uploads?page=${page}&page_size=${size}`
            );
            const data = await response.json();
            if (response.ok && data.uploads) {
                setUploads(data.uploads);
                setHasMore(data.has_more || false);
                setTotalUploads(data.total || 0);
            }
        } catch (error) {
            console.error('Failed to load uploads:', error);
        } finally {
            if (!silent) setLoadingUploads(false);
        }
    };

    // Step 1: File Upload
    const handleFileUpload = (e) => {
        const uploadedFile = e.target.files[0];
        if (!uploadedFile) return;

        const fileExtension = uploadedFile.name.split('.').pop().toLowerCase();

        if (!['csv', 'xlsx', 'xls'].includes(fileExtension)) {
            alert('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
            return;
        }

        setFile(uploadedFile);
        parseFile(uploadedFile);
    };

    const parseFile = (file) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

                if (jsonData.length < 2) {
                    alert('File must contain at least a header row and one data row');
                    return;
                }

                const headerRow = jsonData[0];
                const dataRows = jsonData.slice(1).filter(row => row.some(cell => cell !== null && cell !== ''));

                setHeaders(headerRow);
                setRawData(dataRows);

                // Auto-detect mapping
                const newMapping = { ...mapping };
                headerRow.forEach((header, index) => {
                    const headerLower = header.toString().toLowerCase();

                    if (headerLower.includes('price') || headerLower.includes('cost') || headerLower.includes('wholesale')) {
                        newMapping.price = index.toString();
                    }

                    if (headerLower.includes('upc') || headerLower.includes('ean') || headerLower.includes('gtin') ||
                        headerLower.includes('barcode') || headerLower.includes('asin')) {
                        newMapping.identifier = index.toString();
                    }

                    if (headerLower.includes('image') || headerLower.includes('img') || headerLower.includes('photo') || headerLower.includes('picture')) {
                        newMapping.image = index.toString();
                    }

                    if (headerLower.includes('link') || headerLower.includes('url') || headerLower.includes('sku') || headerLower.includes('source')) {
                        newMapping.source_link = index.toString();
                    }
                });
                setMapping(newMapping);

                setStep(2);
            } catch (error) {
                alert('Failed to parse file. Please ensure it\'s a valid CSV or Excel file.');
                console.error(error);
            }
        };

        reader.readAsBinaryString(file);
    };

    // Step 2: Column Mapping
    const handleMappingChange = (field, value) => {
        setMapping(prev => ({ ...prev, [field]: value }));
    };

    const proceedToValidation = () => {
        if (!mapping.price || !mapping.identifier) {
            alert('Please map both Price and Identifier columns');
            return;
        }

        validateData();
    };

    // Step 3: Data Validation
    const validateData = () => {
        const errors = [];
        const validated = [];

        rawData.forEach((row, index) => {
            const rowNum = index + 2;
            const rowErrors = [];

            const price = row[parseInt(mapping.price)];
            const identifier = row[parseInt(mapping.identifier)];
            const image = mapping.image ? row[parseInt(mapping.image)] : null;
            const source_link = mapping.source_link ? row[parseInt(mapping.source_link)]?.toString().trim() : null;

            // Remove $ and other currency symbols, then parse
            const cleanPrice = price?.toString().replace(/[$,]/g, '').trim();
            const priceNum = parseFloat(cleanPrice);
            if (!cleanPrice || isNaN(priceNum) || priceNum <= 0) {
                rowErrors.push(`Invalid price: "${price}"`);
            }

            const identifierStr = identifier?.toString().replace(/\s+/g, '');
            if (!identifierStr || identifierStr.length < 3) {
                rowErrors.push(`Invalid identifier: "${identifier}"`);
            }

            let detectedType = 'upc';
            if (identifierStr) {
                if (/^B[A-Z0-9]+$/.test(identifierStr)) {
                    detectedType = 'asin';
                } else if (/^\d{12,14}$/.test(identifierStr)) {
                    detectedType = 'upc';
                } else if (/^\d{8,10}$/.test(identifierStr)) {
                    detectedType = 'walmart_id';
                } else if (/^\d{12}$/.test(identifierStr)) {
                    detectedType = 'ebay_id';
                }
            }

            if (rowErrors.length > 0) {
                errors.push({ row: rowNum, errors: rowErrors });
            } else {
                validated.push({
                    row: rowNum,
                    price: priceNum,
                    identifier: identifierStr,
                    identifier_type: mapping.identifier_type === 'auto' ? detectedType : mapping.identifier_type,
                    image: image || null,
                    source_link: source_link || null
                });
            }
        });

        setValidationErrors(errors);
        setValidatedData(validated);
        setStep(3);
    };

    // Step 4: Submit to Backend
    const submitUpload = async () => {
        if (!uploadName.trim()) {
            alert('Please enter an upload name');
            return;
        }

        if (uploads.some(u => u.upload_name.toLowerCase() === uploadName.trim().toLowerCase())) {
            alert('An upload with this name already exists. Please choose a different name.');
            return;
        }

        setSubmitting(true);

        try {
            const CHUNK_SIZE = 2000;
            const chunks = [];
            for (let i = 0; i < validatedData.length; i += CHUNK_SIZE) {
                chunks.push(validatedData.slice(i, i + CHUNK_SIZE));
            }

            // First chunk creates the upload and returns upload_id
            const firstResponse = await authService.authenticatedFetch(
                API_BASE + '/api/wholesale/upload',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        upload_name: uploadName,
                        file_name: file.name,
                        products: chunks[0],
                        is_first_chunk: true,
                        total_products: validatedData.length
                    })
                }
            );

            const firstData = await firstResponse.json();

            if (!firstResponse.ok) {
                if (firstData.error === 'MAX_SCANS_REACHED') {
                    alert(firstData.message);
                    return;
                }
                alert(firstData.error || 'Upload failed');
                return;
            }

            const uploadId = firstData.upload_id;

            // Send remaining chunks
            for (let i = 1; i < chunks.length; i++) {
                setSubmitting(`Uploading... ${Math.round((i / chunks.length) * 100)}%`);

                const response = await authService.authenticatedFetch(
                    API_BASE + '/api/wholesale/upload',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            upload_id: uploadId,
                            products: chunks[i],
                            is_first_chunk: false
                        })
                    }
                );

                if (!response.ok) {
                    alert('Upload failed mid-way. Please try again.');
                    return;
                }
            }

            // Reset form
            setStep(1);
            setFile(null);
            setRawData([]);
            setHeaders([]);
            setMapping({ price: '', identifier: '', identifier_type: 'auto', image: '', source_link: '' });
            setValidatedData([]);
            setValidationErrors([]);
            setUploadName('');
            setActiveTab('uploads');
            loadUploads();

        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const downloadTemplate = () => {
        const template = [
            ['Price', 'UPC/ASIN/SKU', 'Link/SKU (Optional)', 'Image (Optional)'],
            ['15.99', '123456789012', '', 'https://example.com/image.jpg'],
            ['24.50', 'B08ABCD1234', '', ''],
            ['10.00', '987654321098', '', 'https://example.com/image2.jpg']
        ];

        const ws = XLSX.utils.aoa_to_sheet(template);

        // Get the range of the sheet
        const range = XLSX.utils.decode_range(ws['!ref']);

        // Column B (index 1) = UPC/ASIN/SKU — format as text
        for (let row = 1; row <= range.e.r; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 1 });
            if (!ws[cellAddress]) {
                ws[cellAddress] = { v: '', t: 's' };
            }
            ws[cellAddress].t = 's';                // cell type: string
            ws[cellAddress].z = '@';                // number format: text
            ws[cellAddress].v = String(ws[cellAddress].v ?? ''); // ensure value is a string
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'wholesale_scan_template.xlsx');
    };

    const scrollToDeals = () => {
        const dealsSection = document.getElementById('deals-section');
        if (dealsSection) {
            dealsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Package className="text-teal-600" size={40} />
                        Wholesale Scan
                    </h1>
                    <p className="text-gray-600">
                        Bulk scan wholesale catalogs and supplier lists. Quickly identify profitable products with high demand and low competition.
                    </p>
                </div>

                <div className="flex justify-end mb-4">
                    <MegaExport scanType="wholesale" />
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="flex border-b">
                        <button
                            onClick={() => { setActiveTab('upload'); setSelectedUpload(null) }}
                            className={`flex-1 py-4 px-6 font-semibold transition ${activeTab === 'upload'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <Plus className="inline mr-2" size={20} />
                            Upload New CSV
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('uploads');
                                loadUploads();
                            }}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center gap-1 justify-center ${activeTab === 'uploads'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <FileSpreadsheet className="inline mr-2" size={20} />
                            My Uploads ({loadingUploads ? <RefreshCw className="animate-spin" size={16} /> : totalUploads})
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'upload' ? (
                            <UploadTab
                                step={step}
                                setStep={setStep}
                                file={file}
                                fileInputRef={fileInputRef}
                                handleFileUpload={handleFileUpload}
                                downloadTemplate={downloadTemplate}
                                headers={headers}
                                rawData={rawData}
                                mapping={mapping}
                                handleMappingChange={handleMappingChange}
                                proceedToValidation={proceedToValidation}
                                validationErrors={validationErrors}
                                validatedData={validatedData}
                                setValidatedData={setValidatedData}
                                uploadName={uploadName}
                                setUploadName={setUploadName}
                                submitUpload={submitUpload}
                                submitting={submitting}
                                user={user}
                            />
                        ) : (
                            <MyUploadsTab
                                uploads={uploads}
                                setUploads={setUploads}
                                loadingUploads={loadingUploads}
                                onUploadSelect={(upload) => {
                                    setSelectedUpload(upload);
                                    setTimeout(() => scrollToDeals(), 100);
                                }}
                                onUploadDeleted={loadUploads}
                                pageSize={pageSize}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    pageSizeRef.current = size;
                                    setCurrentPage(1);
                                    currentPageRef.current = 1;
                                    loadUploads(false, 1, size);
                                }}
                                currentPage={currentPage}
                                hasMore={hasMore}
                                totalUploads={totalUploads}
                                onPageChange={(page) => {
                                    setCurrentPage(page);
                                    currentPageRef.current = page;
                                    loadUploads(false, page);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Selected Upload Details */}
                {selectedUpload && (
                    <div id="deals-section">
                        <UploadDetailsSection
                            upload={selectedUpload}
                            onClose={() => setSelectedUpload(null)}
                            user={user}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

// Upload Tab Component
const UploadTab = ({
    step,
    setStep,
    file,
    fileInputRef,
    handleFileUpload,
    downloadTemplate,
    headers,
    rawData,
    mapping,
    handleMappingChange,
    proceedToValidation,
    validationErrors,
    validatedData,
    setValidatedData,
    uploadName,
    setUploadName,
    submitUpload,
    submitting,
    user
}) => {
    return (
        <div>
            {/* Progress Steps */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                    {[
                        { num: 1, label: 'Upload File', icon: Upload },
                        { num: 2, label: 'Map Columns', icon: FileSpreadsheet },
                        { num: 3, label: 'Validate & Submit', icon: CheckCircle }
                    ].map((s, idx) => (
                        <React.Fragment key={s.num}>
                            <div className="flex flex-col items-center">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition ${step >= s.num
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-gray-200 text-gray-500'
                                    }`}>
                                    {step > s.num ? <CheckCircle size={24} /> : <s.icon size={24} />}
                                </div>
                                <span className={`mt-2 text-sm font-semibold ${step >= s.num ? 'text-teal-600' : 'text-gray-500'}`}>
                                    {s.label}
                                </span>
                            </div>
                            {idx < 2 && (
                                <div className={`flex-1 h-1 mx-4 transition ${step > s.num ? 'bg-teal-600' : 'bg-gray-200'}`}></div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Step Content */}
            <div className="space-y-6">
                {/* Step 1: Upload */}
                {step === 1 && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">Upload Your Wholesale Catalog</h2>
                            <p className="text-gray-600 mb-6">
                                Upload a CSV or Excel file containing your wholesale products with pricing and identifiers
                            </p>
                        </div>

                        <div
                            className="border-4 border-dashed border-teal-300 rounded-xl p-12 text-center hover:border-teal-500 transition cursor-pointer bg-teal-50"
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-100'); }}
                            onDragEnter={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-100'); }}
                            onDragLeave={(e) => { e.currentTarget.classList.remove('border-teal-500', 'bg-teal-100'); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('border-teal-500', 'bg-teal-100');
                                const droppedFile = e.dataTransfer.files[0];
                                if (droppedFile) handleFileUpload({ target: { files: [droppedFile] } });
                            }}
                        >
                            <Upload className="mx-auto text-teal-600 mb-4" size={64} />
                            <p className="text-xl font-semibold text-gray-800 mb-2">
                                Click to upload or drag and drop
                            </p>
                            <p className="text-gray-600 mb-4">
                                CSV, XLSX, or XLS files accepted
                            </p>
                            {file && (
                                <div className="mt-4 inline-flex items-center gap-2 bg-teal-100 px-4 py-2 rounded-lg">
                                    <FileSpreadsheet className="text-teal-600" size={20} />
                                    <span className="text-teal-800 font-semibold">{file.name}</span>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                        </div>

                        <div className="flex justify-center items-center gap-4">
                            <button
                                onClick={downloadTemplate}
                                className="flex items-center gap-2 text-teal-600 hover:text-teal-800 font-semibold"
                            >
                                <Download size={20} />
                                Download Template File
                            </button>

                            <div className="w-px h-6 bg-gray-300" />

                            <div className="flex items-center gap-2 bg-white border-2 border-teal-500 rounded-lg px-4 py-2">
                                <span className="text-sm font-semibold text-gray-700">Will scan for:</span>
                                {(user?.marketplaces_sell_on?.length > 0) ? (
                                    user.marketplaces_sell_on.map(marketplace => marketplace.toLowerCase().replace(/\s+/g, '-') !== "aip" && (
                                        <img
                                            key={marketplace}
                                            src={`/icons/${marketplace.toLowerCase().replace(/\s+/g, '-')}.png`}
                                            alt={marketplace}
                                            title={marketplace}
                                            className="h-6 object-contain"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ))
                                ) : (
                                    <span className="text-sm text-gray-400">None selected</span>
                                )}
                                <a
                                    href="/settings#marketplaces"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 px-3 py-1.5 rounded-lg transition whitespace-nowrap"
                                >
                                    Change →
                                </a>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold mb-2">Required Columns:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li><strong>Price:</strong> Your wholesale cost per unit</li>
                                        <li><strong>Identifier:</strong> UPC, EAN, GTIN, ASIN, Walmart Item ID, or eBay Item ID</li>
                                        <li><strong>Source Link / SKU (Optional):</strong> Link to the product page/SKU row</li>
                                        <li><strong>Image URL (Optional):</strong> Product image URL for display</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 2: Mapping */}
                {step === 2 && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Map Your Columns</h2>
                            <p className="text-gray-600">
                                Columns have been auto-detected. Verify or adjust the mappings below.
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <p className="text-sm text-gray-600 mb-2">
                                <strong>Preview:</strong> Found {rawData.length} rows with {headers.length} columns
                            </p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-300">
                                            {headers.map((header, idx) => (
                                                <th key={idx} className="px-4 py-2 text-left font-semibold text-gray-700">
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rawData.slice(0, 3).map((row, rowIdx) => (
                                            <tr key={rowIdx} className="border-b border-gray-200">
                                                {row.map((cell, cellIdx) => (
                                                    <td key={cellIdx} className="px-4 py-2 text-gray-600">
                                                        {cell?.toString() || '-'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Price Column <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={mapping.price}
                                    onChange={(e) => handleMappingChange('price', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                >
                                    <option value="">-- Select Column --</option>
                                    {headers.map((header, idx) => (
                                        <option key={idx} value={idx.toString()}>
                                            {header}
                                        </option>
                                    ))}
                                </select>
                                {mapping.price && (
                                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                        <CheckCircle size={14} />
                                        Sample: {rawData[0]?.[parseInt(mapping.price)]}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Identifier Column <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={mapping.identifier}
                                    onChange={(e) => handleMappingChange('identifier', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                >
                                    <option value="">-- Select Column --</option>
                                    {headers.map((header, idx) => (
                                        <option key={idx} value={idx.toString()}>
                                            {header}
                                        </option>
                                    ))}
                                </select>
                                {mapping.identifier && (
                                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                        <CheckCircle size={14} />
                                        Sample: {rawData[0]?.[parseInt(mapping.identifier)]}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Source Link / SKU Column (Optional)
                                </label>
                                <select
                                    value={mapping.source_link}
                                    onChange={(e) => handleMappingChange('source_link', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                >
                                    <option value="">-- None --</option>
                                    {headers.map((header, idx) => (
                                        <option key={idx} value={idx.toString()}>{header}</option>
                                    ))}
                                </select>
                                {mapping.source_link && (
                                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                        <CheckCircle size={14} />
                                        Sample: {rawData[0]?.[parseInt(mapping.source_link)]}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Image URL Column (Optional)
                                </label>
                                <select
                                    value={mapping.image}
                                    onChange={(e) => handleMappingChange('image', e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                >
                                    <option value="">-- None --</option>
                                    {headers.map((header, idx) => (
                                        <option key={idx} value={idx.toString()}>
                                            {header}
                                        </option>
                                    ))}
                                </select>
                                {mapping.image && (
                                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                        <CheckCircle size={14} />
                                        Sample: {rawData[0]?.[parseInt(mapping.image)]}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Identifier Type
                            </label>
                            <select
                                value={mapping.identifier_type}
                                onChange={(e) => handleMappingChange('identifier_type', e.target.value)}
                                className="w-full md:w-1/3 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            >
                                <option value="auto">Auto-Detect</option>
                                <option value="upc">UPC/EAN/GTIN</option>
                                <option value="asin">Amazon ASIN</option>
                                <option value="walmart_id">Walmart Item ID</option>
                                <option value="ebay_id">eBay Item ID</option>
                            </select>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button
                                onClick={() => setStep(1)}
                                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                            >
                                Back
                            </button>
                            <button
                                onClick={proceedToValidation}
                                disabled={!mapping.price || !mapping.identifier}
                                className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continue to Validation
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Validation */}
                {step === 3 && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Data Validation Results</h2>
                            <p className="text-gray-600">
                                Review the validation results before submitting
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="text-green-600" size={32} />
                                    <div>
                                        <p className="text-sm text-green-700 font-semibold">Valid Rows</p>
                                        <p className="text-2xl font-bold text-green-800">{validatedData.length}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                    <XCircle className="text-red-600" size={32} />
                                    <div>
                                        <p className="text-sm text-red-700 font-semibold">Invalid Rows</p>
                                        <p className="text-2xl font-bold text-red-800">{validationErrors.length}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                    <FileSpreadsheet className="text-blue-600" size={32} />
                                    <div>
                                        <p className="text-sm text-blue-700 font-semibold">Total Rows</p>
                                        <p className="text-2xl font-bold text-blue-800">{rawData.length}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {validationErrors.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                                <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                                    <AlertCircle size={20} />
                                    Validation Errors ({validationErrors.length})
                                </h3>
                                <div className="space-y-2">
                                    {validationErrors.map((error, idx) => (
                                        <div key={idx} className="bg-white border border-red-300 rounded p-3">
                                            <p className="font-semibold text-red-700">Row {error.row}:</p>
                                            <ul className="list-disc list-inside text-sm text-red-600 ml-4">
                                                {error.errors.map((err, errIdx) => (
                                                    <li key={errIdx}>{err}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {validatedData.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h3 className="font-semibold text-green-800 mb-3">
                                    Valid Data Preview (First 10 rows)
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm bg-white rounded">
                                        <thead>
                                            <tr className="border-b border-gray-300">
                                                <th className="px-4 py-2 text-left">Row</th>
                                                <th className="px-4 py-2 text-left">Price</th>
                                                <th className="px-4 py-2 text-left">Identifier</th>
                                                <th className="px-4 py-2 text-left">Type</th>
                                                <th className="px-4 py-2 text-left">Image</th>
                                                <th className="px-4 py-2 text-left">SKU/Link</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {validatedData.slice(0, 10).map((item, idx) => (
                                                <tr key={idx} className="border-b border-gray-200">
                                                    <td className="px-4 py-2">{item.row}</td>
                                                    <td className="px-4 py-2 font-semibold text-green-700">${item.price.toFixed(2)}</td>
                                                    <td className="px-4 py-2 font-mono text-sm">{item.identifier}</td>
                                                    <td className="px-4 py-2">
                                                        <select
                                                            value={item.identifier_type}
                                                            onChange={(e) => {
                                                                const newData = [...validatedData];
                                                                const itemIndex = validatedData.findIndex(d => d.row === item.row);
                                                                newData[itemIndex].identifier_type = e.target.value;
                                                                setValidatedData(newData);
                                                            }}
                                                            className="bg-teal-100 text-teal-700 px-2 py-1 rounded text-xs font-semibold border-none cursor-pointer"
                                                        >
                                                            <option value="auto">Auto</option>
                                                            <option value="upc">UPC</option>
                                                            <option value="asin">ASIN</option>
                                                            <option value="walmart_id">Walmart</option>
                                                            <option value="ebay_id">eBay</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {item.image ? (
                                                            <CheckCircle className="text-green-600" size={16} />
                                                        ) : (
                                                            <XCircle className="text-gray-400" size={16} />
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-xs text-gray-600 max-w-[120px] truncate" title={item.source_link || ''}>
                                                        {item.source_link || <XCircle className="text-gray-400" size={16} />}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Upload Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={uploadName}
                                onChange={(e) => setUploadName(e.target.value)}
                                placeholder="e.g., November 2024 Inventory"
                                className="w-full md:w-1/2 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                            <p className="mt-2 text-sm text-gray-600">
                                Give this upload a name so you can find it later
                            </p>
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button
                                onClick={() => setStep(2)}
                                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                            >
                                Back to Mapping
                            </button>
                            <button
                                onClick={submitUpload}
                                disabled={validatedData.length === 0 || !uploadName.trim() || submitting}
                                className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <RefreshCw className="animate-spin" size={20} />
                                        {typeof submitting === 'string' ? submitting : 'Uploading...'}
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle size={20} />
                                        Submit {validatedData.length} Products
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// My Uploads Tab Component
const MyUploadsTab = ({ uploads, setUploads, loadingUploads, onUploadSelect, onUploadDeleted, currentPage, hasMore, totalUploads, onPageChange, pageSize, onPageSizeChange }) => {
    const [deletingUploadId, setDeletingUploadId] = useState(null);
    const [pausingId, setPausingId] = useState(null);
    const [resumingId, setResumingId] = useState(null);
    const [rerunningId, setRerunningId] = useState(null);

    const handlePause = async (uploadId) => {
        setPausingId(uploadId);
        try {
            await authService.authenticatedFetch(API_BASE + `/api/wholesale/uploads/${uploadId}/pause`, {
                method: 'POST'
            });
            setUploads(prev => prev.map(u => u.id === uploadId ? { ...u, status: 'paused' } : u));
        } catch (error) {
            alert('Failed to pause upload');
        } finally {
            setPausingId(null);
        }
    };

    const handleResume = async (uploadId) => {
        setResumingId(uploadId);
        try {
            await authService.authenticatedFetch(API_BASE + `/api/wholesale/uploads/${uploadId}/resume`, {
                method: 'POST'
            });
            setUploads(prev => prev.map(u => u.id === uploadId ? { ...u, status: 'processing' } : u));
        } catch (error) {
            alert('Failed to resume upload');
        } finally {
            setResumingId(null);
        }
    };

    const handleDeleteUpload = async (uploadId) => {
        if (!window.confirm('Are you sure you want to delete this upload? This will delete all associated deals.')) {
            return;
        }
        setDeletingUploadId(uploadId);
        setUploads(prev => prev.filter(u => u.id !== uploadId));
        try {
            await authService.authenticatedFetch(API_BASE + `/api/wholesale/uploads/${uploadId}`, {
                method: 'DELETE'
            });
            onUploadDeleted();
        } catch (error) {
            onUploadDeleted();
            alert('Failed to delete upload');
        } finally {
            setDeletingUploadId(null);
        }
    };

    const handleRerun = async (uploadId) => {
        if (!window.confirm('Rerun this upload? All existing deals will be deleted and products will be rescanned.')) return;
        setRerunningId(uploadId);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/wholesale/uploads/${uploadId}/rerun`,
                { method: 'POST' }
            );
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to rerun');
            onUploadDeleted();
        } catch (error) {
            alert(error.message || 'Failed to rerun upload');
        } finally {
            setRerunningId(null);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <span className="flex items-center gap-1 text-green-700 bg-green-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <CheckCircle size={16} /> Completed
                </span>;
            case 'processing':
                return <span className="flex items-center gap-1 text-blue-700 bg-blue-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <RefreshCw className="animate-spin" size={16} /> Processing
                </span>;
            case 'pending':
                return <span className="flex items-center gap-1 text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <AlertCircle size={16} /> Pending
                </span>;
            case 'paused':
                return <span className="flex items-center gap-1 text-orange-700 bg-orange-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <Pause size={16} /> Paused
                </span>;
            case 'failed':
                return <span className="flex items-center gap-1 text-red-700 bg-red-100 px-3 py-1 rounded-full text-sm font-semibold">
                    <XCircle size={16} /> Failed
                </span>;
            default:
                return null;
        }
    };

    if (loadingUploads) {
        return (
            <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                <p className="text-gray-600 text-lg font-semibold">Loading your uploads...</p>
            </div>
        );
    }

    if (uploads.length === 0) {
        return (
            <div className="text-center py-12">
                <FileSpreadsheet className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">No uploads yet</p>
                <p className="text-gray-500 text-sm">Upload your first CSV to get started!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {[...uploads].sort((a, b) => {
                const order = { processing: 0, pending: 1, paused: 2, completed: 3, failed: 4 };
                return (order[a.status] ?? 5) - (order[b.status] ?? 5);
            }).map(upload => (
                <div key={upload.id} className="bg-white border border-gray-200 rounded-lg hover:shadow-lg transition p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                {upload.upload_name}
                                <span className="text-sm text-gray-600 ml-2">
                                    {new Date(upload.created_at).toLocaleDateString()}
                                </span>
                            </h3>
                            <div className="flex items-center gap-2 mb-3">
                                {getStatusBadge(upload.status)}
                                {upload.file_name && (
                                    <span className="text-sm text-gray-600">
                                        📄 {upload.file_name}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {(upload.status === 'pending' || upload.status === 'processing') && (
                                <button
                                    onClick={() => handlePause(upload.id)}
                                    disabled={pausingId === upload.id}
                                    className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded-lg transition disabled:opacity-50"
                                    title="Pause"
                                >
                                    {pausingId === upload.id ? <RefreshCw className="animate-spin" size={20} /> : <Pause size={20} />}
                                </button>
                            )}
                            {upload.status === 'paused' && (
                                <button
                                    onClick={() => handleResume(upload.id)}
                                    disabled={resumingId === upload.id}
                                    className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition disabled:opacity-50"
                                    title="Resume"
                                >
                                    {resumingId === upload.id ? <RefreshCw className="animate-spin" size={20} /> : <Play size={20} />}
                                </button>
                            )}
                            <button
                                onClick={() => handleRerun(upload.id)}
                                disabled={rerunningId === upload.id}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-1 text-sm"
                                title="Delete deals and rescan all products"
                            >
                                {rerunningId === upload.id ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                                Rerun
                            </button>
                            <button
                                onClick={() => handleDeleteUpload(upload.id)}
                                disabled={deletingUploadId === upload.id}
                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                            >
                                {deletingUploadId === upload.id ? <RefreshCw className="animate-spin" size={20} /> : <Trash2 size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* Status Panel */}
                    <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="grid grid-cols-4 gap-2 mb-4">
                            <div className="text-center p-2 bg-blue-50 rounded">
                                <div className="text-xs text-blue-600 font-semibold">Processing</div>
                                <div className="text-lg font-bold text-blue-800">
                                    {upload.status_breakdown?.pending + upload.status_breakdown?.processing || 0}
                                </div>
                            </div>
                            <div className="text-center p-2 bg-green-50 rounded">
                                <div className="text-xs text-green-600 font-semibold">Completed</div>
                                <div className="text-lg font-bold text-green-800">
                                    {upload.status_breakdown?.completed || 0}
                                </div>
                            </div>
                            <div className="text-center p-2 bg-red-50 rounded">
                                <div className="text-xs text-red-600 font-semibold">Failed</div>
                                <div className="text-lg font-bold text-red-800">
                                    {upload.status_breakdown?.failed || 0}
                                </div>
                            </div>
                            <div className="text-center p-2 bg-purple-100 rounded border border-purple-800">
                                <div className="text-xs text-purple-700 font-semibold">Deals Found</div>
                                <div className="text-xl font-bold text-purple-700">
                                    {upload.deals_found || 0}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* View Deals Button */}
                    <button
                        onClick={() => onUploadSelect(upload)}
                        className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2"
                    >
                        <CheckCircle size={20} />
                        View Deals
                    </button>
                </div>
            ))}

            <p className="text-xs text-gray-300 text-center" style={{ marginBottom: '-4px' }}>
                Running slow? Email contact@multisellerpro.com and we'll speed things up for you.
            </p>

            <div className="flex items-center justify-center gap-2 mt-4">
                <span className="text-sm text-gray-600">Per page:</span>
                <select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-black bg-white"
                >
                    {[5, 7, 10, 20, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
            </div>

            {(currentPage > 1 || hasMore) && (
                <div className="flex items-center justify-center gap-4 mt-4">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                    >
                        ← Previous
                    </button>
                    <span className="text-sm text-black">Page {currentPage}</span>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={!hasMore}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-black hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};

// Upload Details Section Component
const UploadDetailsSection = ({ upload, onClose, user }) => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tempDeals, setTempDeals] = useState([]);
    const [hasNewDeals, setHasNewDeals] = useState(false);
    const [ignoredNewDeals, setIgnoredNewDeals] = useState(false);

    useEffect(() => {
        setDeals([]);
        setTempDeals([]);
        setHasNewDeals(false);
        loadDeals();

        // Auto-refresh every 20 seconds if processing
        const interval = setInterval(() => {
            if (upload.status === 'processing') {
                loadDeals(true);
            }
        }, 20000);

        return () => clearInterval(interval);
    }, [upload.id]);

    const loadDeals = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await authService.authenticatedFetch(
                API_BASE + `/api/wholesale/uploads/${upload.id}/deals`
            );
            const data = await response.json();
            if (response.ok) {
                const incoming = data.deals || [];
                setDeals(prev => {
                    if (prev.length === 0) return incoming;
                    const existingIds = new Set(prev.map(d => d.id));
                    const newOnes = incoming.filter(d => !existingIds.has(d.id));
                    const updated = prev.map(d => {
                        const match = incoming.find(i => i.id === d.id);
                        return match ? match : d;
                    });
                    if (newOnes.length > 0) {
                        setTempDeals(newOnes);
                        setHasNewDeals(true);
                    }
                    return updated;
                });
            }
        } catch (error) {
            console.error('Failed to load deals:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"
            >
                ✕
            </button>
            <UpdateDealsOnboardingModal show={hasNewDeals} scanType="wholesale" />
            {hasNewDeals && !ignoredNewDeals && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-4">
                    <span>🎉 {tempDeals.length} new deal{tempDeals.length > 1 ? 's' : ''} found!</span>
                    <button
                        onClick={() => {
                            setDeals(prev => [...tempDeals, ...prev]);
                            setTempDeals([]);
                            setHasNewDeals(false);
                        }}
                        className="bg-teal-500 hover:bg-teal-400 px-3 py-1 rounded-full text-sm font-semibold"
                    >
                        Update
                    </button>
                    <button
                        onClick={() => {
                            setIgnoredNewDeals(true);
                            setHasNewDeals(false);
                        }}
                        className="text-gray-400 hover:text-white text-sm"
                    >
                        ✕
                    </button>
                </div>
            )}
            <DealsViewer
                deals={deals}
                loading={loading}
                title={upload.upload_name}
                showFilters={true}
                user={user}
            />
        </div>
    );
};

export default WholesaleScanPage;